import { uploadStateAtom } from '@pixum/image-upload-service/lib/upload-state';
import { createUploadServiceForImages } from '@pixum/image-upload-service/lib/create-upload-service-for-images';
import { createUploadImages } from '@pixum/image-upload-service/lib/upload-images';
import {
  getAcceptedFileFormats,
  getAcceptedFileFormatsAsMimeType
} from '@pixum/image-upload-service/lib/get-accepted-file-formats';
import { getMaxFileSizeInMb } from '@pixum/image-upload-service/lib/file-size';
import { createImageResourceService } from '@pixum/image-service/lib/resource/image-resource-service';
import { createImageThumbnailService } from '@pixum/image-thumbnail-service';
import { imageFileValidationService } from '@pixum/image-file-validation-service';
import { createDialogService } from '@pixum/dialog-service';
import { createUploadErrorService } from '@pixum/upload-error-service';
import { createUploadErrorStateActions } from '@pixum/upload-error-service/lib/upload-error-state';
import { uploadStateSelectors } from '@pixum/upload-service';
import type { DialogService, StateService, UploadState } from '@pixum/types';
import type { ImageUploadService } from '@pixum/types/lib/image-upload-service/image-upload-service';
import { ImageServiceClientAdapter } from './ImageServiceClientAdapter';
import { createMapUploadErrorToUploadAction } from './uploadErrorService/mapUploadErrorToUploadAction';
import { uploadErrorStateService } from './uploadErrorStateService/uploadErrorStateService';
import {
  onFileAdded,
  onFileValidation,
  onThumbnail,
  onUploadComplete,
  onUploadError,
  onUploadProgress
} from './eventHandlers';
import type { ImageStateUpdater } from '../../../types/expressDesigner';

export class ExpressDesignerServiceClient {
  readonly imageService: {
    upload: ImageUploadService;
    resource: ReturnType<typeof createImageResourceService>;
  };
  readonly dialogService: DialogService;

  constructor(
    updater: ImageStateUpdater,
    imagesStateService: StateService<unknown>,
    onUploadAllComplete?: () => void,
    constants: Record<string, string> = {}
  ) {
    const imageServiceClient = new ImageServiceClientAdapter();
    const dialogService = createDialogService();
    const uploadErrorStateActions = createUploadErrorStateActions(uploadErrorStateService);

    this.dialogService = dialogService;

    const uploadService = createUploadServiceForImages({
      imageServiceClient,
      stateService: uploadStateAtom,
      onFileAdded: onFileAdded(updater),
      onFileValidation: onFileValidation(updater),
      onUploadProgress: onUploadProgress(updater),
      onUploadComplete: onUploadComplete(updater),
      onUploadError: onUploadError(
        updater,
        uploadErrorStateActions,
        imageServiceClient.getValidationStatus.bind(imageServiceClient)
      ),
      onThumbnail: onThumbnail(updater),
      onUploadAllComplete,
      thumbnailService: createImageThumbnailService(),
      validateFile: imageFileValidationService.validateFile
    });

    this.imageService = {
      upload: {
        getAcceptedFileFormats,
        getAcceptedFileFormatsAsMimeType,
        getMaxFileSizeInMb,
        retryUpload: uploadService.retryUpload,
        uploadImages: createUploadImages({ uploadService })
      },
      resource: createImageResourceService({ imageServiceClient })
    };

    // Filter errored UUIDs from selectUploads to avoid double-counting in numOfFilesToBeUploaded.
    const patchedUploadStateSelectors = {
      ...uploadStateSelectors,
      selectUploads: (state: UploadState) => {
        const errorUuids = new Set(uploadErrorStateService.get().uploadErrors.map(e => e.uuid));

        return uploadStateSelectors.selectUploads(state).filter(u => !errorUuids.has(u.uuid));
      }
    };

    createUploadErrorService({
      mapUploadErrorToUploadAction: createMapUploadErrorToUploadAction({
        dialogService,
        imageStateActions: updater,
        imagesStateService,
        uploadErrorStateActions,
        uploadService: this.imageService.upload,
        constants
      }),
      uploadStateSelectors: patchedUploadStateSelectors,
      uploadStateService: uploadStateAtom,
      uploadErrorStateService
    });
  }
}
